import { MetroConfig, GameConfig } from '../config';
import { Station, Train, Player, MetroLine, ChallengeCard, Timetable } from '../types';
import { connectedStations, metroLines } from '../data/metroData';
import { generateTimetable, getNextTrainArrival, getTimeUntilNextTrain } from '../utils/timetable';
import { calculateDistance } from '../utils/pathfinding';
import { StorageManager } from '../managers/StorageManager';
import { UIScene } from './UIScene';

export class GameScene extends Phaser.Scene {
  private map!: Phaser.GameObjects.Graphics;
  // private backgroundMap!: Phaser.GameObjects.Image;
  private stations: Map<string, Station> = new Map();
  private lines: Map<string, MetroLine> = new Map();
  private trains: Map<string, Train> = new Map();
  private players: Map<string, Player> = new Map();
  private localPlayer!: Player;
  // private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private stationLabels: Map<string, Phaser.GameObjects.Text> = new Map();
  private trainSprites: Map<string, Phaser.GameObjects.Rectangle> = new Map();
  private uiLayer!: Phaser.GameObjects.Container;
  private gameLayer!: Phaser.GameObjects.Container;
  private statusText!: Phaser.GameObjects.Text;
  private coinsText!: Phaser.GameObjects.Text;
  private headStartTimer?: Phaser.Time.TimerEvent;
  private spaceKey!: Phaser.Input.Keyboard.Key;
  private eKey!: Phaser.Input.Keyboard.Key;
  private challengeCards: ChallengeCard[] = [];
  private isHeadStartActive: boolean = false;
  private timetable!: Timetable;
  // private timetableCard: Phaser.GameObjects.Container;
  // private timetableText!: Phaser.GameObjects.Text;
  private zoomLevel: number = 1;
  private isMapDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private isRoundEnded: boolean = false;
  private roundStartTime: number = 0;
  
  // Add storage manager reference
  private storageManager: StorageManager;
  
  constructor() {
    super({ key: 'GameScene' });
    this.storageManager = StorageManager.getInstance();
  }

  preload() {
    // this.load.audio('capture', 'assets/sounds/capture.mp3');
    // this.load.audio('station_arrival', 'assets/sounds/station_arrival.mp3');
    // this.load.audio('train_departure', 'assets/sounds/train_departure.mp3');
}

  init(data: { playerRole: 'runner' | 'chaser' }) {
    // Initialize player
    this.localPlayer = {
      id: 'player1', // In multiplayer, this would be assigned by server
      role: data.playerRole,
      position: { x: 0, y: 0 },
      coins: MetroConfig.initialCoins,
      powerUps: [],
      onTrain: false
    };
    this.players.set(this.localPlayer.id, this.localPlayer);
    
    // Reset collections
    this.stations.clear();
    this.lines.clear();
    this.trains.clear();
    this.stationLabels.clear();
    this.trainSprites.clear();
    
    // Initialize challenge cards
    this.initializeChallengeCards();
  }

  // ********** Game Initialization ********** //
  create() {
    // Make sure input is defined before accessing it
    if (this.input && this.input.keyboard) {
      // this.cursors = this.input.keyboard.createCursorKeys();
      this.spaceKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
      this.eKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.E);
    } else {
      console.error("Input or keyboard is undefined");
    }
    
    // Set world bounds
    const worldWidth = 12000; 
    const worldHeight = 8000;
    this.cameras.main.setBounds(0, 0, worldWidth, worldHeight);
    
    // Create a container for all game elements that should zoom and move
    // Position it in the top-left corner of the world, not centered
    this.gameLayer = this.add.container(0, 0);
    
    // Create UI layer that will stay fixed
    this.uiLayer = this.add.container(0, 0).setDepth(1000);
    
    // Create background - a stylized map of London
    this.createBackgroundMap();
    
    // Create graphics for map elements
    this.map = this.add.graphics();
    this.gameLayer.add(this.map); // Add map to game layer so it zooms
    
    // Initialize metro system
    this.createMetroSystem();
    this.createTrains();
    
    // Create UI elements that should stay fixed (not zoom with the camera)
    this.createUI();
    
    // Create timetable UI
    this.createTimetableUI();
    
    // Start game loop based on player role
    if (this.localPlayer.role === 'runner') {
      this.startRunnerGame();
    } else {
      this.startChaserGame();
    }
    
    // Set initial camera settings
    this.zoomLevel = 0.5; // More moderate zoom initially
    this.cameras.main.setZoom(this.zoomLevel);

    // Center on Oxford Circus (center of the map)
    const centerStation = this.stations.get('oxford-circus');
    if (centerStation) {
      this.cameras.main.centerOn(centerStation.position.x, centerStation.position.y);
    } else {
      // Fallback center
      this.cameras.main.centerOn(worldWidth/2, worldHeight/2);
    }
    
    // Add camera follow with deadzone for smoother movement
    const followTarget = { x: this.localPlayer.position.x, y: this.localPlayer.position.y };
    this.cameras.main.startFollow(
      followTarget, 
      true, 0.09, 0.09
    );
    
    // Update the follow target in the game loop
    this.events.on('update', () => {
      followTarget.x = this.localPlayer.position.x;
      followTarget.y = this.localPlayer.position.y;
    });
    
    // Setup zoom and camera controls
    this.setupCameraControls();
    
    // Create a separate UI camera
    this.createUICamera();
    
    // Ensure the UI is always visible and fixed
    this.cameras.main.ignore(this.uiLayer);
    // this.cameras.main.ignore(this.timetableCard);
  }

  // helper function that can be moved to a separate file
  private createUICamera() {
    // Create a separate camera for UI elements that doesn't move with the game world
    const uiCamera = this.cameras.add(0, 0, window.innerWidth, window.innerHeight);
    uiCamera.setScroll(0, 0);
    uiCamera.setName('uiCamera');
    
    // Set UI camera to only view the UI layers
    uiCamera.ignore(this.gameLayer);
    
    // Make sure UI camera doesn't zoom or move
    uiCamera.setZoom(1);
    uiCamera.setScroll(0, 0);
    uiCamera.setBounds(0, 0, window.innerWidth, window.innerHeight);
    
    // Listen for resize events to adjust the UI camera
    this.scale.on('resize', (gameSize: Phaser.Structs.Size) => {
      uiCamera.setSize(gameSize.width, gameSize.height);
    });
  }

  // ********** Game Loop ********** //
  update() {
    // Update camera follow position if not dragging
    if (!this.isMapDragging) {
      // Let the camera follow the player smoothly
      const camera = this.cameras.main;
      // Check if camera is following something (accessing internal property safely)
      const isFollowing = camera.hasOwnProperty('_follow') && (camera as any)._follow !== null;
      
      if (!isFollowing) {
        camera.centerOn(
          Phaser.Math.Linear(camera.midPoint.x, this.localPlayer.position.x, 0.1),
          Phaser.Math.Linear(camera.midPoint.y, this.localPlayer.position.y, 0.1)
        );
      }
    }
    
    // Update game elements
    this.handleInput();
    this.updatePlayerMovement();
    this.updateTrains();
    this.drawMap();
    this.updateUI();
    
    // Check for tag events
    this.checkForTag();
    
    // Update timetable display if visible
    // if (this.timetableCard.visible) {
      // this.updateTimetableDisplay();
    // }
  }










// ALL OF THIS WILL BE MOVED TO SEPARATE FILES





  // function that can be moved to a separate file
  private createMetroSystem() {
    // Use the London metro data instead of hardcoded data
    
    // Add stations from metroData
    connectedStations.forEach(station => {
      // Create a scaled copy of the station with 10x position coordinates
      const scaledStation = {
        ...station,
        position: {
          x: station.position.x * 10,
          y: station.position.y * 10
        }
      };
      this.stations.set(station.id, scaledStation);
      
      // Create station label
      const label = this.add.text(
        scaledStation.position.x,
        scaledStation.position.y + 60, // Increase offset for labels
        station.name,
        {
          fontSize: '18px', // Increase font size
          color: '#000',
          backgroundColor: '#ffffff80',
          padding: { x: 5, y: 3 } // Increase padding
        }
      ).setOrigin(0.5).setDepth(10);
      
      // Station labels should be affected by zoom, so we don't set scrollFactor
      this.gameLayer.add(label);
      this.stationLabels.set(station.id, label);
    });

    // Add lines from metroData
    metroLines.forEach(line => {
      this.lines.set(line.id, line);
    });

    // Set initial player position at a central station (e.g., Oxford Circus)
    const startStation = this.stations.get('oxford-circus');
    if (startStation && this.localPlayer) {
      this.localPlayer.position = { ...startStation.position };
      this.localPlayer.currentStationId = startStation.id;
    }
    
    // Generate timetable
    this.timetable = generateTimetable(this.lines, this.stations, Date.now(), 60);
  }

  // function that can be moved to a separate file
  private createTrains() {
    // Create trains for each line based on the line's trainCount
    this.lines.forEach((line, lineId) => {
      for (let i = 0; i < line.trainCount; i++) {
        // Calculate initial position and direction
        const direction = i % 2 === 0 ? 1 : -1;
        const startStationIndex = Math.floor(i * line.stations.length / line.trainCount) % line.stations.length;
        
        const trainId = `${lineId}-train-${i + 1}`;
        const train: Train = {
          id: trainId,
          lineId: lineId,
          currentStationIndex: startStationIndex,
          nextStationIndex: startStationIndex + direction,
          direction: direction as 1 | -1, // Cast to satisfy TypeScript
          scheduledDepartureTime: Date.now() + (Math.random() * 5000 + 2000),
          actualDepartureTime: 0, // Will be calculated on first update
          passengers: []
        };
        
        // Calculate actual departure with variance
        const variance = 1000 * MetroConfig.trainScheduleVariance;
        train.actualDepartureTime = train.scheduledDepartureTime + 
                                  (Math.random() * variance * 2 - variance);
                                  
        this.trains.set(train.id, train);
        
        // Create visual representation for train - make it larger for the 10x map
        const lineColor = line.color.replace('#', '0x');
        const trainRect = this.add.rectangle(0, 0, 150, 75, parseInt(lineColor)) // Larger for 10x map
                                .setStrokeStyle(6, 0x000000) // Thicker outline
                                .setDepth(5);
        this.gameLayer.add(trainRect); // Add to game layer so it zooms
        this.trainSprites.set(train.id, trainRect);
      }
    });
  }

  // function that can be moved to a separate file
  private createBackgroundMap() {
    // Create a stylized background of London
    // First create a base color for the map - 10x larger
    const background = this.add.rectangle(6000, 4000, 40000, 40000, 0xf8f8f8).setDepth(0);
    
    // Add the Thames river
    const thames = this.add.graphics().setDepth(1);
    thames.fillStyle(0x6BAED6, 0.7); // Light blue for water
    
    // Draw a curvy Thames path - scaled 10x
    thames.beginPath();
    thames.moveTo(0, 3000);
    thames.lineTo(1000, 3200);
    thames.lineTo(2000, 3100);
    thames.lineTo(3000, 3400);
    thames.lineTo(4000, 3600);
    thames.lineTo(5000, 3500);
    thames.lineTo(6000, 3300);
    thames.lineTo(7000, 3400);
    thames.lineTo(8000, 3600);
    thames.lineTo(9000, 3800);
    // Draw the width of the river
    thames.lineTo(9000, 4200);
    thames.lineTo(8000, 4000);
    thames.lineTo(7000, 3800);
    thames.lineTo(6000, 3700);
    thames.lineTo(5000, 3900);
    thames.lineTo(4000, 4000);
    thames.lineTo(3000, 3800);
    thames.lineTo(2000, 3500);
    thames.lineTo(1000, 3600);
    thames.lineTo(0, 3400);
    thames.closePath();
    thames.fill();
    
    // Add stylized parks (green areas) - scaled 10x
    const parks = this.add.graphics().setDepth(1);
    parks.fillStyle(0x8CCE8A, 0.6); // Light green for parks
    
    // Hyde Park
    parks.fillRect(2400, 1400, 1300, 1000);
    
    // Regent's Park
    parks.fillCircle(4000, 1000, 600);
    
    // Green Park
    parks.fillRect(4000, 2200, 700, 500);
    
    // St James Park
    parks.fillRect(4800, 2800, 800, 400);
    
    // Add some stylized roads - scaled 10x
    const roads = this.add.graphics().setDepth(2);
    roads.lineStyle(80, 0xDDDDDD);
    
    // Main roads
    // Oxford Street
    roads.strokeLineShape(new Phaser.Geom.Line(2000, 1800, 6000, 1800));
    
    // Regent Street
    roads.strokeLineShape(new Phaser.Geom.Line(4200, 800, 4200, 3000));
    
    // Piccadilly
    roads.strokeLineShape(new Phaser.Geom.Line(3000, 2200, 6000, 2200));
    
    // The Strand
    roads.strokeLineShape(new Phaser.Geom.Line(5000, 2700, 6500, 2700));
    
    // Add city landmarks as simple shapes - scaled 10x
    const landmarks = this.add.graphics().setDepth(3);
    
    // Big Ben / Houses of Parliament
    landmarks.fillStyle(0xCD853F);
    landmarks.fillRect(4800, 3000, 300, 300);
    
    // Tower of London
    landmarks.fillStyle(0x708090);
    landmarks.fillRect(6800, 3200, 400, 300);
    
    // St Paul's Cathedral
    landmarks.fillStyle(0xD3D3D3);
    landmarks.fillCircle(6000, 2600, 150);
    
    // Add some text labels for major areas - these should zoom with the map
    const hydeText = this.add.text(3000, 1100, "Hyde Park", { fontSize: '56px', color: '#006400' }).setDepth(3);
    const regentText = this.add.text(4000, 800, "Regent's Park", { fontSize: '56px', color: '#006400' }).setDepth(3);
    const towerText = this.add.text(6800, 3000, "Tower of London", { fontSize: '48px', color: '#333' }).setDepth(3);
    const westText = this.add.text(4700, 3300, "Westminster", { fontSize: '48px', color: '#333' }).setDepth(3);
    const oxfordText = this.add.text(3500, 1600, "Oxford Street", { fontSize: '48px', color: '#333' }).setDepth(3);
    const paulText = this.add.text(3500, 2350, "St Paul's", { fontSize: '48px', color: '#333' }).setDepth(3);
    
    // Make sure all background map elements zoom properly
    this.gameLayer.add([
      background, thames, parks, roads, landmarks,
      hydeText, regentText, towerText, westText, oxfordText, paulText
    ]);
  }

  // This should be moved to CameraControls.ts
  private setupCameraControls() {
    // Setup zoom controls with keyboard
    this.input.keyboard?.on('keydown-MINUS', () => {
      this.zoomLevel = Math.max(0.2, this.zoomLevel - 0.1);
      this.cameras.main.setZoom(this.zoomLevel);
    });
    
    this.input.keyboard?.on('keydown-PLUS', () => {
      this.zoomLevel = Math.min(1.5, this.zoomLevel + 0.1);
      this.cameras.main.setZoom(this.zoomLevel);
    });
    
    // Add mouse wheel zoom functionality
    this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gameObjects: any, _deltaX: number, deltaY: number, _deltaZ: number) => {
      if (deltaY > 0) {
        // Zoom out
        this.zoomLevel = Math.max(0.2, this.zoomLevel - 0.05);
      } else {
        // Zoom in
        this.zoomLevel = Math.min(1.5, this.zoomLevel + 0.05);
      }
      this.cameras.main.setZoom(this.zoomLevel);
    });
    
    // Reset camera button
    this.input.keyboard?.on('keydown-R', () => {
      this.zoomLevel = 0.5; // Start with a more moderate zoom level
      this.cameras.main.setZoom(this.zoomLevel);
      
      // Center on Oxford Circus (center of the map)
      const centerStation = this.stations.get('oxford-circus');
      if (centerStation) {
        this.cameras.main.centerOn(centerStation.position.x, centerStation.position.y);
      } else {
        // Fallback to world center
        this.cameras.main.centerOn(6000, 4000); // Half of world bounds
      }
    });
    
    // Setup mouse drag with any mouse button (not just right button)
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isMapDragging = true;
      this.dragStartX = pointer.x;
      this.dragStartY = pointer.y;
    });
    
    this.input.on('pointerup', () => {
      this.isMapDragging = false;
    });
    
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.isMapDragging) {
        // Adjust drag sensitivity based on zoom level
        const dragFactor = 2.0 / this.zoomLevel; // More sensitive at higher zoom levels
        
        const deltaX = (this.dragStartX - pointer.x) * dragFactor;
        const deltaY = (this.dragStartY - pointer.y) * dragFactor;
        
        this.cameras.main.scrollX += deltaX;
        this.cameras.main.scrollY += deltaY;
        
        this.dragStartX = pointer.x;
        this.dragStartY = pointer.y;
      }
    });
    
    // Initial camera settings
    this.zoomLevel = 0.5; // More moderate zoom level initially
    this.cameras.main.setZoom(this.zoomLevel);
    
    // Center camera on Oxford Circus initially
    const centerStation = this.stations.get('oxford-circus');
    if (centerStation) {
      this.cameras.main.centerOn(centerStation.position.x, centerStation.position.y);
    } else {
      // Fallback to world center
      this.cameras.main.centerOn(6000, 4000);
    }
  }

  // This should be move to UIManager.ts
  private createUI() {
    // Status bar background
    const statusBar = this.add.rectangle(
      this.scale.width / 2,
      30,
      this.scale.width,
      60,
      0x000000,
      0.8
    ).setOrigin(0.5, 0.5);
    
    // Player role and status text
    this.statusText = this.add.text(
      20,
      20,
      `Role: ${this.localPlayer.role.toUpperCase()}`,
      { fontSize: '16px', color: '#ffffff' }
    );
    
    // Coins display
    this.coinsText = this.add.text(
      this.scale.width - 150,
      20,
      `Coins: ${this.localPlayer.coins}`,
      { fontSize: '16px', color: '#ffffff' }
    );
    
    // Controls help - update to only show train controls
    // const controlsText = this.add.text(
    //   this.scale.width / 2,
    //   20,
    //   'SPACE: Board/Exit Train | E: Draw Card | R: Reset View | +/-: Zoom',
    //   { fontSize: '14px', color: '#ffffff' }
    // ).setOrigin(0.5, 0);
    
    // Game Rules text
    const rulesText = this.add.text(
      this.scale.width / 2,
      this.scale.height - 30,
      'METRO TAG: You can only move by using trains! Wait at stations for trains to arrive.',
      { 
        fontSize: '14px', 
        color: '#ffffff',
        backgroundColor: '#000000',
        padding: { x: 10, y: 5 }
      }
    ).setOrigin(0.5);
    
    // London Underground logo
    const undergroundLogo = this.add.text(
      this.scale.width - 50,
      this.scale.height - 30,
      'TFL',
      { 
        fontSize: '24px', 
        fontStyle: 'bold',
        color: '#ffffff',
        backgroundColor: '#FF0000',
        padding: { x: 10, y: 5 }
      }
    ).setOrigin(0.5);
    
    // Line legend
    this.createLineLegend();
    
    // Zone indicator
    const zoneText = this.add.text(
      20,
      this.scale.height - 30,
      'London Zones 1-3',
      {
        fontSize: '14px', 
        color: '#000000',
        backgroundColor: '#FFFFFF',
        padding: { x: 8, y: 4 }
      }
    );
    
    // Add all elements to UI container
    this.uiLayer.add([statusBar, this.statusText, this.coinsText, undergroundLogo, zoneText, rulesText]);
    
    // Handle window resize events to reposition UI elements
    this.scale.on('resize', (gameSize: Phaser.Structs.Size) => {
      statusBar.setPosition(gameSize.width / 2, 30);
      statusBar.width = gameSize.width;
      
      this.coinsText.setPosition(gameSize.width - 150, 20);
      // controlsText.setPosition(gameSize.width / 2, 20);
      undergroundLogo.setPosition(gameSize.width - 50, gameSize.height - 30);
      zoneText.setPosition(20, gameSize.height - 30);
      rulesText.setPosition(gameSize.width / 2, gameSize.height - 30);
    });
  }

  // function that can be moved to a separate file
  private createTimetableUI() {
    // Create a container for the timetable
    // this.timetableCard = this.add.container(0, 0);
    
    // Timetable background
    // const timetableWidth = 320;
    // const timetableHeight = 400;
    // const timetableBg = this.add.rectangle(
    //   this.scale.width - timetableWidth/2 - 20,
    //   this.scale.height/2,
    //   timetableWidth,
    //   timetableHeight,
    //   0x222222,
    //   0.9
    // ).setOrigin(0.5);
    
    // // Timetable header
    // const timetableHeader = this.add.text(
    //   this.scale.width - timetableWidth/2 - 20,
    //   this.scale.height/2 - timetableHeight/2 + 30,
    //   'Departures',
    //   { 
    //     fontSize: '18px', 
    //     fontStyle: 'bold',
    //     color: '#ffffff',
    //     align: 'center'
    //   }
    // ).setOrigin(0.5);
    
    // // Timetable content
    // this.timetableText = this.add.text(
    //   this.scale.width - timetableWidth + 30,
    //   this.scale.height/2 - timetableHeight/2 + 60,
    //   'No station selected',
    //   { 
    //     fontSize: '14px', 
    //     color: '#ffffff',
    //     wordWrap: { width: timetableWidth - 40 }
    //   }
    // );
    
    // Add a close button
    // const closeButton = this.add.text(
    //   this.scale.width - 40,
    //   this.scale.height/2 - timetableHeight/2 + 20,
    //   'X',
    //   {
    //     fontSize: '16px',
    //     color: '#ffffff',
    //     backgroundColor: '#aa0000',
    //     padding: { x: 8, y: 4 }
    //   }
    // ).setOrigin(0.5).setInteractive();
    
    // closeButton.on('pointerdown', () => {
    //   this.timetableCard.setVisible(false);
    // });
    
    // Add a button to toggle timetable visibility
    // const toggleButton = this.add.text(
    //   this.scale.width - 160,
    //   50,
    //   '📋 Timetable',
    //   {
    //     fontSize: '14px',
    //     color: '#ffffff',
    //     backgroundColor: '#000000',
    //     padding: { x: 8, y: 4 }
    //   }
    // ).setOrigin(0, 0.5).setInteractive();
    
    // toggleButton.on('pointerdown', () => {
    //   this.timetableCard.setVisible(!this.timetableCard.visible);
    //   this.updateTimetableDisplay();
    // });
    
    // this.uiLayer.add(toggleButton);
    
    // Add all timetable elements to the container
    // this.timetableCard.add([timetableBg, timetableHeader, this.timetableText, closeButton]);
    
    // Initially hide the timetable
    // this.timetableCard.setVisible(false);
    
    // Handle window resize events
    // this.scale.on('resize', (gameSize: Phaser.Structs.Size) => {
    //   timetableBg.setPosition(gameSize.width - timetableWidth/2 - 20, gameSize.height/2);
    //   timetableHeader.setPosition(gameSize.width - timetableWidth/2 - 20, gameSize.height/2 - timetableHeight/2 + 30);
    //   this.timetableText.setPosition(gameSize.width - timetableWidth + 30, gameSize.height/2 - timetableHeight/2 + 60);
    //   closeButton.setPosition(gameSize.width - 40, gameSize.height/2 - timetableHeight/2 + 20);
    //   // toggleButton.setPosition(gameSize.width - 160, 50);
    // });
    
    // Add timetable container to UI layer to ensure it doesn't scroll with the camera
    // this.uiLayer.add(this.timetableCard);
  }
  
  // private updateTimetableDisplay() {
    // if (!this.timetableCard.visible) return;
    
    // if (this.localPlayer.currentStationId) {
    //   const station = this.stations.get(this.localPlayer.currentStationId);
    //   if (station) {
    //     // Get upcoming departures for this station
    //     const entries = getStationTimetable(this.timetable, station.id, Date.now(), 7);
    //     if (entries.length > 0) {
    //       let content = `${station.name} - Departures:\n\n`;
    //       content += this.formatTimetableEntries(entries);
    //       this.timetableText.setText(content);
    //     } else {
    //       this.timetableText.setText(`No scheduled departures from ${station.name}`);
    //     }
    //   }
    // } else if (this.localPlayer.currentTrainId) {
    //   const train = this.trains.get(this.localPlayer.currentTrainId);
    //   if (train) {
    //     const line = this.lines.get(train.lineId);
    //     if (line) {
    //       // Show current train information
    //       let content = `${line.name} - Current Journey:\n\n`;
          
    //       // Get current and next station
    //       const currentStationId = line.stations[train.currentStationIndex];
    //       const currentStation = this.stations.get(currentStationId);
          
    //       const nextStationId = line.stations[train.nextStationIndex];
    //       const nextStation = this.stations.get(nextStationId);
          
    //       if (currentStation && nextStation) {
    //         const departureTime = new Date(train.actualDepartureTime).toLocaleTimeString();
    //         content += `From: ${currentStation.name}\n`;
    //         content += `To: ${nextStation.name}\n`;
    //         content += `Departure: ${departureTime}\n\n`;
            
    //         // Show next few stops
    //         content += 'Upcoming stops:\n';
    //         let stopIndex = train.nextStationIndex;
    //         const direction = train.direction;
            
    //         for (let i = 0; i < 5; i++) {
    //           stopIndex += direction;
              
    //           if (stopIndex >= 0 && stopIndex < line.stations.length) {
    //             const stopId = line.stations[stopIndex];
    //             const stop = this.stations.get(stopId);
    //             if (stop) {
    //               content += `- ${stop.name}\n`;
    //             }
    //           } else {
    //             break; // End of line
    //           }
    //         }
    //       }
          
    //       this.timetableText.setText(content);
    //     }
    //   }
    // } else {
    //   this.timetableText.setText('Not at a station or on a train');
    // }
  // }

  // private formatTimetableEntries(entries: TimetableEntry[]): string {
  //   let result = 'Time   | Line        | Platform | Direction\n';
  //   result += '-------|-------------|----------|----------\n';
    
  //   entries.forEach(entry => {
  //     const time = new Date(entry.arrivalTime).toLocaleTimeString();
  //     // Extract line ID from train ID (assuming format "lineId-train-X")
  //     const lineId = entry.trainId.split('-')[0];
  //     const line = this.lines.get(lineId);
  //     const lineName = line ? line.name : lineId;
      
  //     // Safely handle platform with null check
  //     const platformStr = typeof entry.platform === 'string' ? entry.platform : '-';
  //     const paddedPlatform = platformStr.padEnd(8);
  //     result += `${time.padEnd(7)} | ${lineName.padEnd(13)} | ${paddedPlatform} | ${entry.direction}\n`;
  //   });
    
  //   return result;
  // }

  // ui function that can be moved to a separate file
  private createLineLegend() {
    // Create a legend for the metro lines in the bottom left
    const legendContainer = this.add.container(20, GameConfig.height - 60);
    legendContainer.setScrollFactor(0);
    this.uiLayer.add(legendContainer);
    
    // Create a background
    const legendBg = this.add.rectangle(
      0, 0, 200, 20, 0xFFFFFF, 0.7
    ).setOrigin(0, 0).setScrollFactor(0);
    legendContainer.add(legendBg);
    
    // Add line colors and names
    const mainLines = ['central', 'northern', 'piccadilly', 'victoria', 'jubilee'];
    
    mainLines.forEach((lineId, index) => {
      const line = this.lines.get(lineId);
      if (line) {
        // Add color square
        const lineColor = parseInt(line.color.replace('#', '0x'));
        const colorSquare = this.add.rectangle(
          10 + index * 40, 
          10, 
          10, 
          10, 
          lineColor
        ).setOrigin(0.5).setScrollFactor(0);
        
        // Add first letter of line name
        const lineLetter = this.add.text(
          10 + index * 40,
          10,
          line.name.charAt(0),
          {
            fontSize: '8px',
            color: lineColor === 0x000000 ? '#ffffff' : '#000000',
            fontStyle: 'bold'
          }
        ).setOrigin(0.5).setScrollFactor(0);
        
        legendContainer.add([colorSquare, lineLetter]);
      }
    });
  }

  // unsure if this function can be moved to a separate file
  private handleInput() {
    // Handle boarding/exiting trains with SPACE key
    if (Phaser.Input.Keyboard.JustDown(this.spaceKey)) {
      if (this.localPlayer.currentTrainId) {
        this.exitTrain();
      } else if (this.localPlayer.currentStationId) {
        this.boardTrain();
      }
    }
    
    // Handle drawing challenge cards with E key
    if (Phaser.Input.Keyboard.JustDown(this.eKey)) {
      if (this.localPlayer.currentStationId && !this.localPlayer.currentTrainId) {
        this.drawChallengeCard();
      }
    }
  }

  // unsure if this function can be moved to a separate file
  private updatePlayerMovement() {
    // Check if player is on a train
    if (this.localPlayer.currentTrainId) {
      // Player is on a train, movement is handled by train
      const train = this.trains.get(this.localPlayer.currentTrainId);
      const trainSprite = train ? this.trainSprites.get(train.id) : undefined;
      
      if (trainSprite) {
        this.localPlayer.position = { x: trainSprite.x, y: trainSprite.y };
      }
      return;
    }
    
    // Players cannot move directly - they must use trains
    // Check if we're at a station (for UI purposes)
    this.checkStationArrival();
  }

  private updateTrains() {
    const currentTime = Date.now();
    
    this.trains.forEach(train => {
      const line = this.lines.get(train.lineId);
      if (!line) return;
      
      // Get current and next station info
      const currentStationId = line.stations[train.currentStationIndex];
      const nextStationId = line.stations[train.nextStationIndex]; 
      const currentStation = this.stations.get(currentStationId);
      const nextStation = this.stations.get(nextStationId);
      
      if (!currentStation || !nextStation) return;
      
      const trainSprite = this.trainSprites.get(train.id);
      if (!trainSprite) return;
      
      // If train hasn't departed yet, position it at current station
      if (currentTime < train.actualDepartureTime) {
        trainSprite.setPosition(currentStation.position.x, currentStation.position.y);
        return;
      }
      
      // Calculate train movement between stations
      // Use fixed journey time (closer to original implementation)
      const journeyTime = 5000; // Restore original 5-second journey time
      
      const elapsedTime = currentTime - train.actualDepartureTime;
      
      if (elapsedTime < journeyTime) {
        // Train is moving between stations
        const progress = elapsedTime / journeyTime;
        const x = Phaser.Math.Linear(currentStation.position.x, nextStation.position.x, progress);
        const y = Phaser.Math.Linear(currentStation.position.y, nextStation.position.y, progress);
        trainSprite.setPosition(x, y);
        
        // Update passengers positions if any
        train.passengers.forEach(passengerId => {
          const passenger = this.players.get(passengerId);
          if (passenger) {
            passenger.position = { x, y };
          }
        });
      } else {
        // Train has arrived at next station
        train.currentStationIndex = train.nextStationIndex;
        train.nextStationIndex += train.direction;
        
        // Reverse direction if at end of line
        if (train.nextStationIndex >= line.stations.length || train.nextStationIndex < 0) {
          train.direction *= -1;
          train.nextStationIndex = train.currentStationIndex + train.direction;
        }
        
        // Schedule next departure with original timing
        const baseDelay = 5000; // 5 seconds at station
        const variance = baseDelay * 0.2; // 20% variance (from original MetroConfig)
        train.scheduledDepartureTime = currentTime + baseDelay;
        train.actualDepartureTime = train.scheduledDepartureTime + 
                                   (Math.random() * variance * 2 - variance);
                                    
        // Handle passenger disembarkation
        this.announceTrainArrival(train);
      }
    });
  }

  private drawMap() {
    this.map.clear();

    // Draw metro lines
    this.lines.forEach(line => {
      const lineColor = parseInt(line.color.replace('#', '0x'));
      this.map.lineStyle(40, lineColor, 0.8); // 10x thicker lines (was 16)
      
      // Draw line segments between connected stations
      for (let i = 0; i < line.stations.length - 1; i++) {
        const stationA = this.stations.get(line.stations[i]);
        const stationB = this.stations.get(line.stations[i + 1]);
        
        if (stationA && stationB) {
          // Station positions are already scaled 10x in createMetroSystem
          this.map.beginPath();
          this.map.moveTo(stationA.position.x, stationA.position.y);
          this.map.lineTo(stationB.position.x, stationB.position.y);
          this.map.strokePath();
        }
      }
    });

    // Draw stations
    this.stations.forEach(station => {
      // Station positions are already scaled 10x in createMetroSystem
      const stationX = station.position.x;
      const stationY = station.position.y;
      
      // Draw station circle - bigger for interchanges
      const radius = station.isInterchange ? MetroConfig.stationRadius * 2.5 : MetroConfig.stationRadius * 2;
      
      // White fill for all stations
      this.map.fillStyle(0xffffff);
      this.map.fillCircle(stationX, stationY, radius);
      
      // Black border for all stations
      this.map.lineStyle(4, 0x000000); // Thicker border
      this.map.strokeCircle(stationX, stationY, radius);
      
      // For interchange stations, add a special marking
      if (station.isInterchange) {
        this.map.lineStyle(2, 0x000000);
        this.map.strokeCircle(stationX, stationY, radius - 6);
      }
      
      // Draw colored dots for each line that serves this station
      if (station.lines.length > 0) {
        const dotRadius = 6; // Larger dots
        const dotDistance = radius - 10;
        
        station.lines.forEach((lineId, index) => {
          const line = this.lines.get(lineId);
          if (line) {
            const lineColor = parseInt(line.color.replace('#', '0x'));
            const angle = (Math.PI * 2 * index) / station.lines.length;
            const dotX = stationX + Math.cos(angle) * dotDistance;
            const dotY = stationY + Math.sin(angle) * dotDistance;
            
            this.map.fillStyle(lineColor);
            this.map.fillCircle(dotX, dotY, dotRadius);
          }
        });
      }
      
      // Update station labels based on zoom
      const label = this.stationLabels.get(station.id);
      if (label) {
        // Update label position to match the scale - already scaled in createMetroSystem
        label.setPosition(stationX, stationY + 60); // Increase vertical offset
        
        // Adjust label size and visibility based on zoom
        if (this.zoomLevel < 0.3) {
          label.setVisible(!!station.isInterchange); // Only show interchange stations when zoomed out
          label.setFontSize(16);
        } else {
          label.setVisible(true); // Show all when zoomed in
          label.setFontSize(18);
        }
      }
    });

    // Draw local player - player position is already scaled in updatePlayerMovement
    const playerColor = this.localPlayer.role === 'runner' ? 0x00cc00 : 0xcc0000;
    this.map.fillStyle(playerColor);
    this.map.fillCircle(this.localPlayer.position.x, this.localPlayer.position.y, 60); // Larger player circle
    this.map.lineStyle(8, 0x000000); // Thicker outline
    this.map.strokeCircle(this.localPlayer.position.x, this.localPlayer.position.y, 60);
    
    // Draw AI players if any (for singleplayer testing)
    this.players.forEach(player => {
      if (player.id !== this.localPlayer.id) {
        const color = player.role === 'runner' ? 0x00cc00 : 0xcc0000;
        this.map.fillStyle(color);
        this.map.fillCircle(player.position.x, player.position.y, 60); // Larger circle
        this.map.lineStyle(8, 0x000000); // Thicker outline
        this.map.strokeCircle(player.position.x, player.position.y, 60);
      }
    });
  }

  private updateUI() {
    // Update status text
    let statusText = `Role: ${this.localPlayer.role.toUpperCase()}`;
    
    if (this.isHeadStartActive && this.headStartTimer) {
      statusText += ` | Head Start: ${Math.ceil(this.headStartTimer.getRemaining() / 1000 || 0)}s`;
    }

    if (this.localPlayer.currentStationId) {
      const station = this.stations.get(this.localPlayer.currentStationId);
      if (station) {
        statusText += ` | At: ${station.name}`;
        
        // Add next train information
        const nextTrain = getNextTrainArrival(this.timetable, station.id, undefined, Date.now());
        if (nextTrain) {
          const line = Array.from(this.lines.values()).find(l => nextTrain.trainId.startsWith(l.id));
          if (line) {
            const timeUntil = getTimeUntilNextTrain(this.timetable, station.id, line.id, Date.now());
            statusText += ` | Next ${line.name}: ${timeUntil}s`;
          }
        }
      }
    }
    
    if (this.localPlayer.currentTrainId) {
      const train = this.trains.get(this.localPlayer.currentTrainId);
      statusText += ` | On: ${train ? train.lineId : 'Train'}`;
    }
    
    this.statusText.setText(statusText);
    
    // Update coins display
    this.coinsText.setText(`Coins: ${this.localPlayer.coins}`);
  }

  private checkForTag() {
    // Skip if head start is active or if we're in an end-round state
    if (this.isHeadStartActive || this.isRoundEnded) return;
    
    // Find runners and chasers
    let runners: Player[] = [];
    let chasers: Player[] = [];
    
    this.players.forEach(player => {
      if (player.role === 'runner') runners.push(player);
      if (player.role === 'chaser') chasers.push(player);
    });
    
    // Check for tag at stations
    for (const runner of runners) {
      for (const chaser of chasers) {
        // Check if both are at the same station
        if (runner.currentStationId && 
            chaser.currentStationId && 
            runner.currentStationId === chaser.currentStationId) {
          // Ensure they're actually at the station (not just having the ID set)
          const runnerStation = this.stations.get(runner.currentStationId);
          const chaserStation = this.stations.get(chaser.currentStationId);
          
          if (runnerStation && chaserStation) {
            // Calculate distance between players to ensure they're really close
            const distance = calculateDistance(runner.position, chaser.position);
            
            // Tag only if within a small radius (10 pixels)
            if (distance < 10) {
              console.log(`Tag detected at station ${runner.currentStationId}! Distance: ${distance}`);
              this.onTagEvent(runner, chaser);
              return; // Process only one tag event
            } else {
              console.log(`Players at same station but distance is ${distance}`);
            }
          }
        }
        
        // Check if both are on the same train
        if (runner.currentTrainId && 
            chaser.currentTrainId && 
            runner.currentTrainId === chaser.currentTrainId) {
          // Tag detection on trains is automatic (confined space)
          console.log(`Tag detected on train ${runner.currentTrainId}!`);
          this.onTagEvent(runner, chaser);
          return; // Process only one tag event
        }
      }
    }
  }
  
  private onTagEvent(runner: Player, chaser: Player) {
    if (this.isRoundEnded) return; // Prevent multiple triggers
    
    this.isRoundEnded = true;
    console.log('Tag event triggered!', runner, chaser);
    
    // Calculate how long the runner evaded capture
    const evasionTime = Date.now() - this.roundStartTime;
    const evasionSeconds = Math.floor(evasionTime / 1000);
    
    // Update leaderboard
    const runnerName = runner.id === this.localPlayer.id ? 'Player' : 'AI';
    this.updateLeaderboard(runnerName, evasionSeconds);
    
    // Show round end UI
    this.showRoundEndUI(evasionSeconds);
    
    // Try to play capture sound if available
    try {
      this.sound.play('capture', { volume: 0.5 });
    } catch (error) {
      console.warn('Sound not available:', error);
    }
    
    // Log the capture event
    console.log(`Runner was tagged! Evaded for ${evasionSeconds} seconds.`);
  }
  
  private showRoundEndUI(evasionTime: number) {
    // Create a semi-transparent overlay
    const overlay = this.add.rectangle(
      this.scale.width / 2,
      this.scale.height / 2,
      this.scale.width,
      this.scale.height,
      0x000000,
      0.7
    ).setScrollFactor(0).setDepth(100);
    
    // Round end message
    const message = this.localPlayer.role === 'runner' 
      ? `You were tagged! You evaded capture for ${evasionTime} seconds.`
      : `You caught the runner! They evaded capture for ${evasionTime} seconds.`;
    
    const roundEndText = this.add.text(
      this.scale.width / 2,
      this.scale.height / 3,
      message,
      {
        fontSize: '24px',
        color: '#ffffff',
        align: 'center'
      }
    ).setOrigin(0.5).setScrollFactor(0).setDepth(100);
    
    // Show leaderboard
    this.showLeaderboard();
    
    // Continue prompt
    const continueText = this.add.text(
      this.scale.width / 2,
      this.scale.height * 0.8,
      'Press ENTER to continue and swap roles',
      {
        fontSize: '18px',
        color: '#ffffff',
        align: 'center'
      }
    ).setOrigin(0.5).setScrollFactor(0).setDepth(100);
    
    // Add enter key listener - ensure keyboard is defined
    if (this.input.keyboard) {
      const enterKey = this.input.keyboard.addKey('ENTER');
      enterKey.once('down', () => {
        // Clean up UI elements
        overlay.destroy();
        roundEndText.destroy();
        continueText.destroy();
        
        // Reset and swap roles
        this.swapRolesAndRestart();
      });
    } else {
      // Fallback if keyboard input is not available
      this.time.delayedCall(10000, () => {
        // Clean up UI elements after 10 seconds
        overlay.destroy();
        roundEndText.destroy();
        continueText.destroy();
        
        // Reset and swap roles
        this.swapRolesAndRestart();
      });
    }
  }
  
  private swapRolesAndRestart() {
    // Swap player's role
    const newRole = this.localPlayer.role === 'runner' ? 'chaser' : 'runner';
    
    // Restart the scene with swapped roles
    this.scene.restart({ playerRole: newRole });
  }
  
  private updateLeaderboard(playerName: string, time: number) {
    // Get role from the runner player object
    const role = 'runner'; // Runners are always the ones being timed
    
    // Get UI scene to save the score
    const uiScene = this.scene.get('UIScene') as UIScene;
    if (uiScene) {
      uiScene.saveScore(playerName, time, role);
    }
    
    // For backward compatibility, also update the registry
    if (!this.registry.has('leaderboard')) {
      this.registry.set('leaderboard', []);
    }
    
    // Get current registry leaderboard
    const leaderboard = this.registry.get('leaderboard') as {name: string, time: number}[];
    
    // Add new entry
    leaderboard.push({ name: playerName, time: time });
    
    // Sort by time (descending)
    leaderboard.sort((a, b) => b.time - a.time);
    
    // Keep only top 10
    if (leaderboard.length > 10) {
      leaderboard.length = 10;
    }
    
    // Update registry
    this.registry.set('leaderboard', leaderboard);
  }
  
  private showLeaderboard() {
    // Get UI scene to show the leaderboard
    const uiScene = this.scene.get('UIScene') as UIScene;
    if (uiScene) {
      uiScene.showLeaderboard('runner'); // Show runner-specific leaderboard
      return;
    }
    
    // Fallback to old implementation if UIScene not available
    // Get leaderboard data
    const leaderboard = this.registry.get('leaderboard') as {name: string, time: number}[] || [];
    
    // Create leaderboard container
    const container = this.add.container(
      this.scale.width / 2,
      this.scale.height / 2,
      []
    ).setScrollFactor(0).setDepth(100);
    
    // Add title
    const title = this.add.text(
      0,
      -120,
      'LEADERBOARD',
      {
        fontSize: '28px',
        color: '#ffffff',
        align: 'center'
      }
    ).setOrigin(0.5);
    container.add(title);
    
    // Add entries
    let yPos = -80;
    leaderboard.forEach((entry, index) => {
      const text = this.add.text(
        0,
        yPos,
        `${index + 1}. ${entry.name}: ${entry.time} seconds`,
        {
          fontSize: '18px',
          color: '#ffffff',
          align: 'center'
        }
      ).setOrigin(0.5);
      container.add(text);
      yPos += 30;
    });
    
    // If no entries yet
    if (leaderboard.length === 0) {
      const noDataText = this.add.text(
        0,
        -40,
        'No records yet!',
        {
          fontSize: '18px',
          color: '#ffffff',
          align: 'center'
        }
      ).setOrigin(0.5);
      container.add(noDataText);
    }
  }

  private initializeChallengeCards() {
    // Create a deck of challenge cards
    this.challengeCards = [
      { id: 'card1', type: 'bonus', effect: 'Speed Boost', duration: 10000, target: 'runner' },
      { id: 'card2', type: 'bonus', effect: 'Reveal Chasers', duration: 5000, target: 'runner' },
      { id: 'card3', type: 'bonus', effect: 'Extra Coins', duration: 0, target: 'chaser' },
      { id: 'card4', type: 'curse', effect: 'Slow Movement', duration: 8000, target: 'chaser' },
      { id: 'card5', type: 'curse', effect: 'Station Lockdown', duration: 15000, target: 'all' }
    ];
    
    // Shuffle cards
    this.challengeCards = Phaser.Utils.Array.Shuffle(this.challengeCards);
  }

  private exitTrain() {
    if (!this.localPlayer.currentTrainId) return;
    
    const train = this.trains.get(this.localPlayer.currentTrainId);
    if (!train) return;
    
    // Remove player from train passengers
    train.passengers = train.passengers.filter(id => id !== this.localPlayer.id);
    
    // Set player position to current train position
    const trainSprite = this.trainSprites.get(train.id);
    if (trainSprite) {
      this.localPlayer.position = { 
        x: trainSprite.x, 
        y: trainSprite.y 
      };
    }
    
    this.localPlayer.currentTrainId = undefined;
    
    // Check if player is at a station
    this.checkStationArrival();
    
    this.showNotification('Exited train');
  }

  private boardTrain() {
    if (!this.localPlayer.currentStationId) return;
    
    const station = this.stations.get(this.localPlayer.currentStationId);
    if (!station) return;
    
    // Find a train at this station
    let trainToBoard: Train | undefined;
    
    this.trains.forEach(train => {
      const line = this.lines.get(train.lineId);
      if (!line) return;
      
      const currentStationId = line.stations[train.currentStationIndex];
      if (currentStationId === station.id) {
        // Check if train is currently at the station (not departed yet)
        if (Date.now() < train.actualDepartureTime) {
          trainToBoard = train;
        }
      }
    });
    
    if (trainToBoard) {
      // CNeeds to be fixed in the future, not for nowheck if player has enough coins (only for chasers)
      // if (this.localPlayer.role === 'chaser' && this.localPlayer.coins < MetroConfig.ticketCost) {
      //   this.showNotification('Not enough coins for a ticket!', 0xff0000);
      //   return;
      // }
      
      // // Deduct coins for chasers
      // if (this.localPlayer.role === 'chaser') {
      //   this.localPlayer.coins -= MetroConfig.ticketCost;
      // }
      
      // Board the train
      this.localPlayer.currentTrainId = trainToBoard.id;
      trainToBoard.passengers.push(this.localPlayer.id);
      
      this.showNotification(`Boarded ${this.lines.get(trainToBoard.lineId)?.name || 'train'}`);
    } else {
      this.showNotification('No train at station', 0xff0000);
    }
  }

  private drawChallengeCard() {
    if (this.challengeCards.length === 0) {
      this.showNotification('No more challenge cards available!', 0xff0000);
      return;
    }
    
    // Draw top card
    const card = this.challengeCards.pop();
    if (!card) return;
    
    // Check if card affects current player
    const affectsPlayer = (
      card.target === 'all' || 
      card.target === this.localPlayer.role
    );
    
    // Show card effect
    const effectColor = card.type === 'bonus' ? 0x2ecc71 : 0xe74c3c;
    this.showNotification(`Card: ${card.effect} (${card.type})`, effectColor);
    
    // Apply card effect
    if (affectsPlayer) {
      this.applyCardEffect(card);
    }
  }

  private checkStationArrival() {
    if (!this.localPlayer || this.localPlayer.currentTrainId) return;

    // Reset current station
    this.localPlayer.currentStationId = undefined;

    // Check if player is at any station
    this.stations.forEach(station => {
      const distance = Phaser.Math.Distance.Between(
        this.localPlayer.position.x,
        this.localPlayer.position.y,
        station.position.x,
        station.position.y
      );

      // Since the map is 10x larger, use a larger detection radius
      const detectionRadius = MetroConfig.stationRadius * 6; // Increased for 10x scale
      
      if (distance < detectionRadius) {
        if (this.localPlayer.currentStationId !== station.id) {
          this.localPlayer.currentStationId = station.id;
          this.onStationArrival(station);
        }
      }
    });
  }

  private announceTrainArrival(train: Train) {
    const line = this.lines.get(train.lineId);
    if (!line) return;
    
    const stationId = line.stations[train.currentStationIndex];
    const station = this.stations.get(stationId);
    if (!station) return;
    
    // For each passenger, check if they want to exit
    train.passengers.forEach(passengerId => {
      const passenger = this.players.get(passengerId);
      if (passenger && passenger.id === this.localPlayer.id) {
        this.showNotification(`Arrived at ${station.name}`, 0x3498db);
      }
    });
  }

  private onStationArrival(station: Station) {
    // Display station arrival notification
    const notification = this.add.text(
      this.localPlayer.position.x,
      this.localPlayer.position.y - 40,
      `Arrived at ${station.name}`,
      { 
        fontSize: '16px', 
        color: '#000000',
        backgroundColor: '#ffffff', 
        padding: { x: 8, y: 4 } 
      }
    ).setOrigin(0.5).setDepth(20);
    
    // Add notification to game layer so it appears in the world
    this.gameLayer.add(notification);
    
    // Fade out and destroy notification
    this.tweens.add({
      targets: notification,
      alpha: 0,
      y: notification.y - 20,
      duration: 1500,
      onComplete: () => notification.destroy()
    });
    
    // Update timetable if visible
    // if (this.timetableCard.visible) {
    //   this.updateTimetableDisplay();
    // }
    
  }

  private applyCardEffect(card: ChallengeCard) {
    // Apply different effects based on card type
    switch (card.effect) {
      case 'Speed Boost':
        // We can't directly modify readonly properties, but we can apply a speed multiplier to the player
        if (this.localPlayer.role === 'runner') {
          // Store original movement speed value
          // const originalSpeed = MetroConfig.runnerSpeed;
          // Apply multiplier directly in the movement calculations temporarily
          this.localPlayer.powerUps.push({
            id: 'speed_boost',
            type: 'speed_boost',
            duration: card.duration,
            strength: 1.5
          });
          
          this.time.delayedCall(card.duration, () => {
            // Remove the power-up when expired
            this.localPlayer.powerUps = this.localPlayer.powerUps.filter(p => p.id !== 'speed_boost');
            this.showNotification('Speed boost ended');
          });
        }
        break;
        
      case 'Reveal Chasers':
        // TODO: Reveal chaser positions
        break;
        
      case 'Extra Coins':
        // Add coins for chasers
        if (this.localPlayer.role === 'chaser') {
          this.localPlayer.coins += 100;
        }
        break;
        
      case 'Slow Movement':
        // Apply slowdown to chaser
        if (this.localPlayer.role === 'chaser') {
          // Apply multiplier directly in the movement calculations temporarily
          this.localPlayer.powerUps.push({
            id: 'slow_movement',
            type: 'speed_boost',
            duration: card.duration,
            strength: 0.7
          });
          
          this.time.delayedCall(card.duration, () => {
            // Remove the power-up when expired
            this.localPlayer.powerUps = this.localPlayer.powerUps.filter(p => p.id !== 'slow_movement');
            this.showNotification('Movement speed restored');
          });
        }
        break;
        
      case 'Station Lockdown':
        // TODO: Lock a random station
        break;
    }
  }

  private showNotification(message: string, color: number = 0x2ecc71) {
    const notification = this.add.text(
      this.scale.width / 2,
      this.scale.height - 100,
      message,
      { 
        fontSize: '18px', 
        color: '#ffffff',
        backgroundColor: '#' + color.toString(16).padStart(6, '0'), 
        padding: { x: 15, y: 8 } 
      }
    ).setOrigin(0.5).setDepth(100);
    
    // Add to UI layer to make sure it stays fixed
    this.uiLayer.add(notification);
    
    // Fade out and destroy notification
    this.tweens.add({
      targets: notification,
      alpha: 0,
      y: notification.y - 30,
      duration: 2000,
      onComplete: () => notification.destroy()
    });
  }

  private getRunner(): Player | undefined {
    // Helper function to get the runner player
    for (const [, player] of this.players) {
      if (player.role === 'runner') {
        return player;
      }
    }
    return undefined;
  }

  private startRunnerGame() {
    console.log('Starting game as Runner');
    
    // Reset round state
    this.isRoundEnded = false;
    this.roundStartTime = Date.now();

    // Set initial position for the runner (local player) at Oxford Circus
    const startStation = this.stations.get('oxford-circus');
    if (startStation) {
      this.localPlayer.position = { ...startStation.position };
      this.localPlayer.currentStationId = startStation.id;
    }
    
    // Add AI chasers for testing single player
    const aiChaser: Player = {
      id: 'ai-chaser',
      role: 'chaser',
      position: { x: 0, y: 0 },
      coins: MetroConfig.initialCoins,
      powerUps: [],
      onTrain: false
    };
    
    // Place AI chaser at a different station initially (to prevent instant tag)
    // Use a station far from Oxford Circus to give proper time
    const chaserStartStation = this.stations.get('kings-cross'); // Changed from piccadilly-circus
    if (chaserStartStation) {
      aiChaser.position = { ...chaserStartStation.position };
      aiChaser.currentStationId = chaserStartStation.id;
    } else {
      // Fallback to another station if kings-cross doesn't exist
      const fallbackStation = this.stations.get('piccadilly-circus');
      if (fallbackStation) {
        aiChaser.position = { ...fallbackStation.position };
        aiChaser.currentStationId = fallbackStation.id;
      }
    }
    
    this.players.set(aiChaser.id, aiChaser);
    
    // Start head start timer
    this.isHeadStartActive = true;
    this.showNotification(`Head start active! ${MetroConfig.headStartDuration/1000} seconds to escape!`, 0xffaa00);
    
    this.headStartTimer = this.time.delayedCall(
      MetroConfig.headStartDuration,
      () => {
        this.isHeadStartActive = false;
        this.showNotification('Head start over! The chase begins!', 0xff0000);
        this.startChaserAI();
      }
    );
  }

  private startChaserGame() {
    console.log('Starting game as Chaser');
    
    // Reset round state
    this.isRoundEnded = false;
    this.roundStartTime = Date.now();
    
    // Set initial position for the chaser (local player) at Oxford Circus
    const startStation = this.stations.get('oxford-circus');
    if (startStation) {
      this.localPlayer.position = { ...startStation.position };
      this.localPlayer.currentStationId = startStation.id;
    }
    
    // Add AI runner for testing single player
    const aiRunner: Player = {
      id: 'ai-runner',
      role: 'runner',
      position: { x: 0, y: 0 },
      coins: 0, // Runner doesn't need coins
      powerUps: [],
      onTrain: false
    };
    
    // Place AI runner at a different station initially (to prevent instant tag)
    // Use a station that's farther away to prevent immediate tags
    const runnerStartStation = this.stations.get('kings-cross'); // Changed from piccadilly-circus
    if (runnerStartStation) {
      aiRunner.position = { ...runnerStartStation.position };
      aiRunner.currentStationId = runnerStartStation.id;
    } else {
      // Fallback to another station if kings-cross doesn't exist
      const fallbackStation = this.stations.get('piccadilly-circus');
      if (fallbackStation) {
        aiRunner.position = { ...fallbackStation.position };
        aiRunner.currentStationId = fallbackStation.id;
      }
    }
    
    this.players.set(aiRunner.id, aiRunner);
    
    // Start the AI runner movement with head start
    this.isHeadStartActive = true;
    this.showNotification(`AI Runner has a ${MetroConfig.headStartDuration/1000} seconds head start!`, 0xffaa00);
    
    // Allow the runner AI to start immediately
    this.startRunnerAI();
    
    // Delay chaser movement until head start is over
    this.headStartTimer = this.time.delayedCall(
      MetroConfig.headStartDuration,
      () => {
        this.isHeadStartActive = false;
        this.showNotification('Head start over! Begin the chase!', 0xff0000);
      }
    );
  }

  private startChaserAI() {
    // Simplify and make chaser AI more aggressive
    this.time.addEvent({
      delay: 500, // Update more frequently (was 1000ms)
      callback: () => {
        // Skip if head start is active or round has ended
        if (this.isHeadStartActive || this.isRoundEnded) return;
        
        this.players.forEach(player => {
          if (player.role === 'chaser' && player.id !== this.localPlayer.id) {
            const runner = this.getRunner();
            if (!runner) return;
            
            console.log(`AI Chaser state: at station=${player.currentStationId}, on train=${player.currentTrainId}`);
            
            // If already on a train, check if we should get off
            if (player.currentTrainId) {
              const train = this.trains.get(player.currentTrainId);
              if (!train) return;
              
              const line = this.lines.get(train.lineId);
              if (!line) return;
              
              const currentStationId = line.stations[train.currentStationIndex];
              const trainSprite = this.trainSprites.get(train.id);
              
              // Only exit at stations (not between)
              if (currentStationId && trainSprite && train.currentStationIndex === train.nextStationIndex) {
                // ALWAYS exit if runner is at this station, or if at an interchange
                const station = this.stations.get(currentStationId);
                const isInterchange = station?.isInterchange || false;
                const runnerAtThisStation = runner.currentStationId === currentStationId;
                
                // Higher probability to exit at stations (80%)
                const shouldExit = Math.random() < 0.8 || runnerAtThisStation || isInterchange;
                
                if (shouldExit) {
                  // Exit the train
                  train.passengers = train.passengers.filter(id => id !== player.id);
                  player.position = { x: trainSprite.x, y: trainSprite.y };
                  player.currentTrainId = undefined;
                  player.currentStationId = currentStationId;
                  console.log(`AI Chaser exited train at ${currentStationId}`);
                }
              }
            } 
            // If at a station, try to board a train to chase the runner
            else if (player.currentStationId) {
              // Find available trains at this station
              let availableTrains: Train[] = [];
              
              this.trains.forEach(train => {
                const line = this.lines.get(train.lineId);
                if (!line) return;
                
                const stationId = line.stations[train.currentStationIndex];
                if (stationId === player.currentStationId && 
                    Date.now() < train.actualDepartureTime &&
                    train.currentStationIndex === train.nextStationIndex) {
                  availableTrains.push(train);
                }
              });
              
              if (availableTrains.length > 0) {
                // Determine runner's location
                let targetStationId = runner.currentStationId;
                
                // If runner is on a train, predict where they're heading
                if (!targetStationId && runner.currentTrainId) {
                  const runnerTrain = this.trains.get(runner.currentTrainId);
                  if (runnerTrain) {
                    const runnerLine = this.lines.get(runnerTrain.lineId);
                    if (runnerLine) {
                      // Target the station the runner's train is heading to
                      const nextStationIndex = runnerTrain.nextStationIndex;
                      if (nextStationIndex >= 0 && nextStationIndex < runnerLine.stations.length) {
                        targetStationId = runnerLine.stations[nextStationIndex];
                      }
                    }
                  }
                }
                
                // Choose best train to board
                let bestTrain: Train | null = null;
                
                if (targetStationId) {
                  // Look for trains heading toward runner
                  for (const train of availableTrains) {
                    const line = this.lines.get(train.lineId);
                    if (!line) continue;
                    
                    if (line.stations.includes(targetStationId)) {
                      // Check if we're going in the right direction
                      const currentIndex = line.stations.indexOf(player.currentStationId);
                      const targetIndex = line.stations.indexOf(targetStationId);
                      
                      if ((train.direction === 1 && targetIndex > currentIndex) ||
                          (train.direction === -1 && targetIndex < currentIndex)) {
                        // This train is moving toward the runner!
                        bestTrain = train;
                        console.log(`AI Chaser found direct train to runner at ${targetStationId}`);
                        break;
                      }
                    }
                  }
                }
                
                // If no direct train to runner found, pick one leading to an interchange
                if (!bestTrain) {
                  for (const train of availableTrains) {
                    const line = this.lines.get(train.lineId);
                    if (!line) continue;
                    
                    // Check next 2 stations on this line
                    const currentIndex = line.stations.indexOf(player.currentStationId);
                    
                    for (let i = 1; i <= 2; i++) {
                      const checkIndex = currentIndex + (i * train.direction);
                      if (checkIndex >= 0 && checkIndex < line.stations.length) {
                        const nextStationId = line.stations[checkIndex];
                        const nextStation = this.stations.get(nextStationId);
                        
                        if (nextStation && nextStation.isInterchange) {
                          // Found interchange station - good for transfers
                          bestTrain = train;
                          console.log(`AI Chaser boarding train to interchange at ${nextStation.name}`);
                          break;
                        }
                      }
                    }
                    
                    if (bestTrain) break;
                  }
                }
                
                // If still no train, just pick any train
                if (!bestTrain && availableTrains.length > 0) {
                  bestTrain = availableTrains[0];
                  console.log(`AI Chaser boarding random train ${bestTrain.id}`);
                }
                
                // Board the selected train
                if (bestTrain) {
                  player.currentTrainId = bestTrain.id;
                  bestTrain.passengers.push(player.id);
                  console.log(`AI Chaser boarded train ${bestTrain.id} to chase runner`);
                }
              }
            }
          }
        });
      },
      loop: true
    });
  }

  private startRunnerAI() {
    // Update runner AI to only use trains for movement
    const aiRunner = this.players.get('ai-runner');
    if (!aiRunner) return;
    
    // Ensure TypeScript knows this is a Player
    const runner = aiRunner as Player;
    
    // Initial station is already set in startChaserGame
    
    // Try to board/exit trains periodically
    this.time.addEvent({
      delay: 3000,
      callback: () => {
        // Skip if round has ended
        if (this.isRoundEnded) return;
        
        // If on a train, occasionally get off
        if (runner.currentTrainId) {
          const train = this.trains.get(runner.currentTrainId);
          if (!train) return;
          
          const line = this.lines.get(train.lineId);
          if (!line) return;
          
          const currentStationId = line.stations[train.currentStationIndex];
          const trainSprite = this.trainSprites.get(train.id);
          
          // Only exit at stations (not between)
          if (currentStationId && trainSprite && train.currentStationIndex === train.nextStationIndex) {
            // Exit with increased chance at interchange stations (to transfer)
            const station = this.stations.get(currentStationId);
            const isInterchange = station?.isInterchange || false;
            
            // Exit with higher probability at interchanges (50%) or normal stations (30%)
            const shouldExit = Math.random() < (isInterchange ? 0.5 : 0.3);
            
            if (shouldExit) {
              // Exit the train
              train.passengers = train.passengers.filter(id => id !== runner.id);
              runner.position = { x: trainSprite.x, y: trainSprite.y };
              runner.currentTrainId = undefined;
              runner.currentStationId = currentStationId;
              console.log(`AI Runner exited train at ${currentStationId}`);
            }
          }
        }
        // If at a station, try to board a train
        else if (runner.currentStationId) {
          const currentStation = this.stations.get(runner.currentStationId);
          if (!currentStation) return;
          
          // Find trains at this station
          let availableTrains: Train[] = [];
            
          this.trains.forEach(train => {
            const line = this.lines.get(train.lineId);
            if (!line) return;
            
            const stationId = line.stations[train.currentStationIndex];
            if (stationId === runner.currentStationId && 
                Date.now() < train.actualDepartureTime &&
                train.currentStationIndex === train.nextStationIndex) { // Only board stationary trains
              availableTrains.push(train);
            }
          });
          
          if (availableTrains.length > 0) {
            // Try to find the chaser to evade
            let chaserToEvade: Player | undefined;
            
            // Find a chaser player
            for (const [_, player] of this.players.entries()) {
              if (player.role === 'chaser') {
                chaserToEvade = player;
                break;
              }
            }
            
            // Choose a strategic train if possible
            let bestTrain = availableTrains[0]; // Default to first train
            let bestScore = -1;
            
            // If we know the chaser's position, try to move away from them
            if (chaserToEvade && chaserToEvade.currentStationId) {
              const chaserStation = this.stations.get(chaserToEvade.currentStationId);
              
              if (chaserStation) {
                // Evaluate each available train
                availableTrains.forEach(train => {
                  const line = this.lines.get(train.lineId);
                  if (!line) return;
                  
                  // Find where this train is heading
                  const nextStationIndex = train.currentStationIndex + train.direction;
                  if (nextStationIndex >= 0 && nextStationIndex < line.stations.length) {
                    const nextStationId = line.stations[nextStationIndex];
                    const nextStation = this.stations.get(nextStationId);
                    
                    if (nextStation) {
                      // Calculate distance from chaser to next station
                      const distanceFromChaser = calculateDistance(
                        chaserStation.position, 
                        nextStation.position
                      );
                      
                      // Higher score is better (farther from chaser)
                      if (distanceFromChaser > bestScore) {
                        bestScore = distanceFromChaser;
                        bestTrain = train;
                      }
                    }
                  }
                });
              }
            } else {
              // No chaser info, choose a train that goes to an interchange
              bestTrain = this.chooseStrategicTrain(availableTrains, runner.currentStationId);
            }
            
            // Board the selected train
            runner.currentTrainId = bestTrain.id;
            bestTrain.passengers.push(runner.id);
            console.log(`AI Runner boarded train ${bestTrain.id} on line ${bestTrain.lineId}`);
          }
        }
      },
      loop: true
    });
  }
  
  // Helper method to choose a strategic train for the runner
  private chooseStrategicTrain(trains: Train[], currentStationId: string): Train {
    // Default to first train
    if (trains.length === 0) {
      throw new Error("No trains available");
    }
    
    let bestTrain = trains[0];
    let bestInterchangeDistance = -1;
    
    // Try to find a train that heads to an interchange
    trains.forEach(train => {
      const line = this.lines.get(train.lineId);
      if (!line) return;
      
      // Check where this train is heading
      const currentIndex = line.stations.indexOf(currentStationId);
      let nextInterchangeIndex = -1;
      
      // Look ahead in the train's direction to find the next interchange
      for (let i = 1; i < line.stations.length; i++) {
        const checkIndex = currentIndex + (i * train.direction);
        
        // Make sure we're within the line's bounds
        if (checkIndex >= 0 && checkIndex < line.stations.length) {
          const stationId = line.stations[checkIndex];
          const station = this.stations.get(stationId);
          
          if (station && station.isInterchange) {
            nextInterchangeIndex = checkIndex;
            break;
          }
        } else {
          break; // Out of bounds
        }
      }
      
      // If we found an interchange ahead
      if (nextInterchangeIndex !== -1) {
        const interchangeDistance = Math.abs(nextInterchangeIndex - currentIndex);
        
        // Prefer trains with closer interchanges (they give more options sooner)
        if (bestInterchangeDistance === -1 || interchangeDistance < bestInterchangeDistance) {
          bestInterchangeDistance = interchangeDistance;
          bestTrain = train;
        }
      }
    });
    
    return bestTrain;
  }

  // Add this helper function to work with timetables
//   private getStationTimetable(timetable: any, stationId: string, currentTime: number, limit: number = 5): any[] {
//     // If no timetable exists, return empty array
//     if (!timetable || !timetable.entries) {
//       return [];
//     }
    
//     // Filter timetable entries for this station that haven't departed yet
//     const relevantEntries = timetable.entries.filter((entry: any) => {
//       return entry.stationId === stationId && 
//              entry.departureTime > currentTime;
//     });
    
//     // Sort by departure time (ascending)
//     relevantEntries.sort((a: any, b: any) => a.departureTime - b.departureTime);
    
//     // Return limited number of entries
//     return relevantEntries.slice(0, limit);
//   }

  // Called when scene is about to be put to sleep
  sleep() {
    console.log('GameScene: sleep');
    
    // Pause any ongoing gameplay timers or animations
    this.scene.pause();
  }
  
  // Called when scene wakes up from sleep
  wake() {
    console.log('GameScene: wake');
    
    // Resume gameplay
    this.scene.resume();
    
    // Reset state if needed for game resumption
    // If we're coming back from menu, ensure proper setup
    if (this.scene.get('MenuScene').scene.isSleeping()) {
      // We're returning from the menu, make sure UI is configured for gameplay
      this.events.emit('game-resumed');
    }
  }
} 